import { STOCK_SCHEMA, INVENTORY_SCHEMA, BUSINESS_SCHEMA } from "../schema";
import { uuid } from "../../oscar-pos-core/constants";
import { unitConversion } from "../../constants";
import { getInvByStockID } from "./inventory";
import { Q } from "@nozbe/watermelondb";
import { database } from "../index";

const inventory = database.collections.get(INVENTORY_SCHEMA);
const stockModal = database.collections.get(STOCK_SCHEMA);


export const getStockByCatalogID = async (id, catalog_id) => await stockModal.query(Q.where("catalog_id", catalog_id), Q.on(BUSINESS_SCHEMA, "id", id)).fetch();

export const getUnArchivedStock = (business_id) => stockModal.query(Q.where("business_id", business_id), Q.where("archived", Q.notEq(true))).observe();
export const observeStockByID = (id) => stockModal.query(Q.where("id", id)).observe();

export const getImagesToSyncStock = async () => await stockModal.query(Q.where('image_url', Q.like('%data:image%'))).fetch();

export const fetchUnArchivedStockCount = () => stockModal.query(Q.where("archived", Q.notEq(true))).fetchCount();

export const getStockByBarcode = (id, code) => stockModal.query(Q.on(BUSINESS_SCHEMA, "id", id), Q.where("bar_code", code));

export const getStocksByBusiness = async (id) => await stockModal.query(Q.on(BUSINESS_SCHEMA, "id", id)).fetch();

export const getStockByID = async (id) => await stockModal.find(id);

export const observeUnArchivedStock = (business_id) =>
  stockModal
    .query(
      Q.where("business_id", business_id),
      Q.where("archived", Q.notEq(true))
    )
    .observeWithColumns(['selling_price', 'stock']);

export const handleEditItemInDB = async (details) => {
  await database.write(async () => {
    const stock = await stockModal.find(details.id);

    stock.stock != details.stock &&
      (await inventory.create((item) => {
        item.stock.set(stock);
        item._raw.id = uuid();
        item.name = details.name;
        item.quantity = Math.abs(stock.stock - details.stock);
        item.type = details.stock < stock.stock ? "out" : "in";
        item.transacted_at = new Date().getTime();
      }));

    await stock.update((item) => {
      item.name = details.name;
      item.stock = details.stock;
      item.cost_price = details.cost_price;
      item.selling_price = details.selling_price;
      details.unit && (item.unit = details.unit);
      item.stock_categories.set(details.category);
      item.bar_code = details.bar_code;
    });
  });
};

export const bulkUpdateStock = async (items, type, from) => {
  await database.write(async () => {
    const allRecords = await prepareUpdation(items, type, from);
    await database.batch(...allRecords);
  });
};

const prepareUpdation = async (items, type, fromPos) => {
  return await Promise.all(
    items.map(async (item) => {
      if ((fromPos && item.stock.id) || (!fromPos && item?.id)) {
        try {
          const c = await stockModal.find(fromPos ? item.stock.id : item?.id);

          return c.prepareUpdate((con) => {
            con.stock =
              type == "in"
                ? (fromPos ? item.totalStock : item.stock) - item.quantity
                : (fromPos ? item.totalStock : item.stock) + item.quantity;
          });
        } catch (err) {
        }
      }
    })
  );
};

export const bulkUpdateStockV2 = async (items, type) => {
  await database.write(async () => {
    const allRecords = await prepareUpdationV2(items, type);
    await database.batch(...allRecords);
  });
};

const prepareUpdationV2 = async (items, type) => {
  return await Promise.all(items.map(async (item) => {
    try {
      let stock = await getStockByID(item.id);
      let unit = item?.updated_unit || stock.unit;
      let qty = unitConversion(stock, unit, item.quantity ?? item.qty ?? 0);

      if (!Number.isFinite(qty)) {
        return;
      }

      return stock.prepareUpdate(
        (i) => (i.stock = type == "in" ? i.stock - qty : i.stock + qty)
      );
    } catch (err) {
    }
  })
  );
};


export const bulkStockUpdation = async (items) => {

  try {
    await database.write(async () => {
      const allRecords = await prepareBulkStockUpdation(items);
      await database.batch(...allRecords.flat(1));
    });
  } catch (error) {
    console.error(`Bulk Stock Updation ERROR: ${error}`);
  }


}

const prepareBulkStockUpdation = async (items) => {
  try {
    return await Promise.all(
      items.map(async (item) => {
        try {
          let stock_item = await getStockByID(item.item_id);
          const inventoryOperation = await inventory.prepareCreate(inventory => {
            inventory.stock.set(stock_item);
            inventory.is_secondary_unit = item.is_secondary_unit;
            inventory.unit_conversion = item.unit_conversion;
            inventory.quantity = item.quantity;
            inventory.price = item.price;
            inventory.transacted_at = item.transacted_at;
            inventory.type = item.type;
            inventory.name = item.name;
            inventory._raw.id = uuid();
          });
          const stockUpdateOperation = stock_item.prepareUpdate((i) => {
            i.stock = item.stock;
          });
          return [inventoryOperation, stockUpdateOperation];

        } catch (err) {
          console.error(`Error updating item with ID ${item.item_id}:`, err);
          return null;
        }
      })
    );
  } catch (err) {
    console.error("Error in bulkStockUpdation:", err);
  }
};


export const bulkStockCreation = async (items, business) => {
  try {
    await database.write(async () => {
      const allRecords = await prepareBulkStockCreation(items, business);
      await database.batch(...allRecords);
    });
  } catch (error) {
    console.error(`Bulk Stock Creation ERROR: ${error}`);
  }
}

const prepareBulkStockCreation = async (items, business) => {
  try {
    return await Promise.all(
      items.map(async (item) => {
        try {
          const stockOperation = await stockModal.prepareCreate((stock) => {
            stock.business.set(business)
            item.category && stock.stock_categories.set(item.category);
            stock._raw.id = item?.id ? item.id : uuid();
            stock.name = item.name;
            stock.stock = item.stock;
            stock.cost_price = item.cost_price;
            stock.selling_price = item.selling_price;
            stock.created_at = new Date().getTime();
            stock.unit = item.unit;
            stock.tax = item.tax;
            stock.inclusive = item.inclusive || true;
            stock.secondary_unit = item.secondary_unit;
            stock.unit_conversion = item.unit_conversion;
            stock.imei = item.imei;
            stock.batch_number = item.batch_number;
            stock.barcode = item.barcode;
            stock.wholesale_price = item.wholesale_price;
            stock.expiry_date = item.expiry_date;
            stock.manufacturing_date = item.manufacturing_date;
            stock.minimum_order_quantity = item.minimum_order_quantity;
            stock.image_url = item.image_url;
            stock.low_stock = item.low_stock;
            stock.expiry_alert = item.expiry_alert;
          });
          return stockOperation;

        } catch (err) {
          console.error(`Error Creating item with name ${item.name}:`, err);
          return null;
        }
      })
    );
  } catch (err) {
    console.error("Error in bulkStockUpdation:", err);
  }
};

export const bulkDeleteStockByBusiness = async (business_id) => {
  await database.write(async () => {
    let stocks = await getStocksByBusiness(business_id);
    const allRecords = await prepareDeletion(stocks);
    const InvRecords = await prepareInvDeletion(stocks);
    await database.batch(...allRecords, ...InvRecords);
  });
};

const prepareDeletion = async (stocks) => {
  return await Promise.all(
    stocks.map(async (stock) => {
      try {
        return stock.prepareMarkAsDeleted();
      } catch (err) {
      }
    })
  );
};

const prepareInvDeletion = async (stocks) => {
  return await Promise.all(stocks.map(async (stock) => {
    let c = await getInvByStockID(stock.id)

    if (c.length) {
      c.map((i) => {
        return i.prepareMarkAsDeleted();
      });
    } else {
      return;
    }
  })
  );
};

export const bulkCreateStock = async (items, business, category) => {
  await database.write(async () => {
    const allRecords = await prepareCreation(items, business, category);
    await database.batch(...allRecords);
  });
};

const prepareCreation = async (items, business, category) => {
  return await Promise.all(
    items.map(async (item) => {
      try {
        let catalogStock = item.catalog_id
          ? await getStockByCatalogID(business.id, item.catalog_id)
          : [];
        if (catalogStock && catalogStock.length) {
          let item = catalogStock[0];

          if (item.archived) {
            return item.prepareUpdate((stock) => {
              stock.archived = false;
            });
          } else {
            return;
          }
        } else {
          return stockModal.prepareCreate((stock) => {
            stock.business.set(business);
            category && stock.stock_categories.set(category);
            stock._raw.id = item.id ? item.id : uuid();
            stock.name = item.name;
            stock.catalog_id = item.catalog_id ? item.catalog_id : null;
            stock.image_url = item.image_url ? item.image_url : null;
            stock.stock = item.stock;
            stock.cost_price = item.selling_price;
            stock.selling_price = item.cost_price;
            stock.created_at = new Date().getTime();
            stock.unit = item.unit;
            item.bar_code && (stock.bar_code = item.bar_code);
          });
        }
      } catch (err) {
      }
    })
  );
};

export const bulkCreateStockV2 = async (items, business) => {
    return await database.write(async () => {
        const allRecords = await prepareCreationV2(items, business);
        await database.batch(...allRecords);
        return allRecords;
    });
};


const prepareCreationV2 = async (items, business) => {
    return await Promise.all(items.map(async details => {
        try {
            return stockModal.prepareCreate(stock => {
                stock.business.set(business);
                stock.minimum_order_quantity = details.minimum_order_quantity;
                stock.manufacturing_date = details.manufacturing_date;
                stock._raw.id = details.id ? details.id : uuid();
                stock.unit_conversion = details.unit_conversion;
                stock.wholesale_price = details.wholesale_price;
                stock.secondary_unit = details.secondary_unit;
                stock.selling_price = details.selling_price;
                stock.batch_number = details.batch_number;
                stock.expiry_alert = details.expiry_alert;
                stock.expiry_date = details.expiry_date;
                stock.created_at = new Date().getTime();
                stock.cost_price = details.cost_price;
                stock.inclusive = details.inclusive;
                stock.image_url = details.image_url;
                stock.low_stock = details.low_stock;
                stock.barcode = details.barcode;
                stock.stock = details.stock;
                stock.name = details.name;
                stock.unit = details.unit;
                stock.imei = details.imei;
                stock.tax = details.tax;
            });
        } catch (err) {
            console.log('ERROR: CREATING-STOCK', err);
        }
    }));
};

export const stockCalculation = async (item) => {
  return new Promise((resolve, reject) => {
    if (item?.is_open_item) {
      resolve([item, false]);
    } else {
      getStockByID(item.id)
        .then((stock) => {
          let isSecondayOne = item?.updated_unit
            ? Boolean(item.updated_unit == stock?.secondary_unit)
            : false;
          let isSecondayTwo = stock?.unit
            ? Boolean(stock.unit == stock?.secondary_unit)
            : false;

          let is_secondary = Boolean(isSecondayOne || isSecondayTwo);

          resolve([stock, is_secondary]);
        })
        .catch((error) => reject(error));
    }
  });
};

export const updateStockSellPrice = async (id, selling_price) => {
  await database.write(async () => {
    const stock = await stockModal.find(id);
    await stock.update((item) => {
      item.selling_price = selling_price;
    });
  });
};

export const updateStockCostPrice = async (id, cost_price) => {
  await database.write(async () => {
    const stock = await stockModal.find(id);
    await stock.update((item) => {
      item.cost_price = cost_price;
    });
  });
};

